import { Col, Row } from "antd";
import classNames from "classnames";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  FRANCE_DEPARTMENTS,
  GENDER,
} from "utils/constants";
import { bindInputProps, bindWebTextFieldInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Head from "../Components/Head";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Radio from "../Components/Radio";
import Select from "../Components/Select";
import InputChecked from "../Components/svg/check_small.svg";
import InputError from "../Components/svg/error.svg";
import validation from "./validation";

const View = ({ initialValues, locked, onSubmit, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = t("DETAIL.COUNTRIES", { returnObjects: true });
  const TRANSLATION = t("B2C_LMF.RELATED_PERSONS", { returnObjects: true });
  const { isValid, handleSubmit, setFieldValue, values, ...formProps } =
    useFormik({
      initialValues,
      onSubmit,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
          returnObjects: true,
        }),
        {
          hasPartner: !isEmpty(get(initialValues, "spouse")),
        },
      ),
    });

  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <>
      <Layout>
        <Head
          description={TRANSLATION.DESCRIPTION}
          title={TRANSLATION.TITLE}
          onBack={onBack}
        />

        <div className="__LMF--content LMF-content lmfRelated__persons lmf__form">
          <Row gutter={[16, 16]} justify={"center"}>
            <Col xs={24} md={18} lg={16}>
              <div className="grid--1 gap-32">
                {get(values, "spouse") && (
                  <div className="lmfRelated__persons--children">
                    <span className="lmfRelated__persons--children_title">
                      {TRANSLATION.SPOUSE}
                    </span>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={24} md={24} lg={24}>
                        <Radio
                          type="simple"
                          disabled={false}
                          values={[
                            {
                              value: GENDER.MALE,
                              label: TRANSLATION.GENDER.OPTIONS.MR,
                            },
                            {
                              value: GENDER.FEMALE,
                              label: TRANSLATION.GENDER.OPTIONS.MRS,
                            },
                          ]}
                          {...bindInputProps({
                            name: `spouse.identity.user.gender`,
                            values,
                            setFieldValue,
                            ...formProps,
                          })}
                          onChange={(value) => {
                            setFieldValue(`spouse.identity.user.gender`, value);
                          }}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Input
                          label={TRANSLATION.LAST_NAME.LABEL}
                          placeholder={TRANSLATION.LAST_NAME.PLACEHOLDER}
                          {...bindWebTextFieldInputProps({
                            name: `spouse.identity.user.lastname`,
                            values,
                            setFieldValue,
                            ...formProps,
                          })}
                          className={classNames({
                            valid: get(values, "spouse.identity.user.lastname"),
                          })}
                          suffix={
                            get(
                              formProps.errors,
                              `spouse.identity.user.lastname`,
                            ) ? (
                              <img src={InputError} alt="" />
                            ) : (
                              <img src={InputChecked} alt="" />
                            )
                          }
                          disabled={locked}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Input
                          label={TRANSLATION.FIRST_NAME.LABEL}
                          placeholder={TRANSLATION.FIRST_NAME.PLACEHOLDER}
                          {...bindWebTextFieldInputProps({
                            name: `spouse.identity.user.firstname`,
                            values,
                            setFieldValue,
                            ...formProps,
                          })}
                          className={classNames({
                            valid: get(
                              values,
                              "spouse.identity.user.firstname",
                            ),
                          })}
                          suffix={
                            get(
                              formProps.errors,
                              `spouse.identity.user.firstname`,
                            ) ? (
                              <img src={InputError} alt="" />
                            ) : (
                              <img src={InputChecked} alt="" />
                            )
                          }
                          disabled={locked}
                        />
                      </Col>
                      <Col xs={24} sm={24} md={24} lg={24}>
                        <Select
                          label={TRANSLATION.BIRTH_COUNTRY.LABEL}
                          placeholder={TRANSLATION.BIRTH_COUNTRY.PLACEHOLDER}
                          options={COUNTRIES.map((name) => ({
                            label: name,
                            value: name,
                          }))}
                          showSearch
                          allowClear
                          defaultValue={get(
                            initialValues,
                            `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                          )}
                          {...bindInputProps({
                            name: `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                            values,
                            ...formProps,
                          })}
                          onChange={(value) => {
                            if (value !== "France")
                              setFieldValue(
                                `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                                "Autre/Etranger",
                              );
                            setFieldValue(
                              `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                              value,
                            );
                          }}
                          className={classNames({
                            valid: get(
                              values,
                              `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                            ),
                          })}
                          suffix={
                            get(
                              formProps.errors,
                              `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                            ) ? (
                              <img src={InputError} alt="" />
                            ) : (
                              <img src={InputChecked} alt="" />
                            )
                          }
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Input
                          label={TRANSLATION.BIRTH_CITY.LABEL}
                          placeholder={TRANSLATION.BIRTH_CITY.PLACEHOLDER}
                          {...bindWebTextFieldInputProps({
                            name: `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                            values,
                            setFieldValue,
                            ...formProps,
                          })}
                          className={classNames({
                            valid: get(
                              values,
                              `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                            ),
                          })}
                          suffix={
                            get(
                              formProps.errors,
                              `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                            ) ? (
                              <img src={InputError} alt="" />
                            ) : (
                              <img src={InputChecked} alt="" />
                            )
                          }
                        />
                      </Col>
                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Select
                          label={TRANSLATION.BIRTH_DEPARTMENT.LABEL}
                          placeholder={TRANSLATION.BIRTH_DEPARTMENT.PLACEHOLDER}
                          showSearch
                          allowClear
                          options={FRANCE_DEPARTMENTS.map(({ code, name }) => ({
                            label: `${code && `(${code})`} ${name}`,
                            value: name,
                          }))}
                          defaultValue={get(
                            initialValues,
                            `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                          )}
                          {...bindInputProps({
                            name: `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                            values,
                            ...formProps,
                          })}
                          className={classNames({
                            valid: get(
                              values,
                              `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                            ),
                          })}
                          onChange={(value) => {
                            setFieldValue(
                              `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                              value,
                            );
                          }}
                          suffix={
                            get(
                              formProps.errors,
                              `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                            ) ? (
                              <img src={InputError} alt="" />
                            ) : (
                              <img src={InputChecked} alt="" />
                            )
                          }
                        />
                      </Col>
                      <Col xs={24} sm={24} md={24} lg={24}>
                        <Input
                          label={TRANSLATION.SOCIAL_SECURITY_NUMBER.LABEL}
                          placeholder={
                            TRANSLATION.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                          }
                          trim={true}
                          defaultValue={get(
                            initialValues,
                            `spouse.identity.socialSecurityNumber`,
                          )}
                          {...bindInputProps({
                            name: `spouse.identity.socialSecurityNumber`,
                            values,
                            ...formProps,
                          })}
                          className={classNames({
                            valid: get(
                              values,
                              `spouse.identity.socialSecurityNumber`,
                            ),
                          })}
                          suffix={
                            get(
                              formProps.errors,
                              `spouse.identity.socialSecurityNumber`,
                            ) ? (
                              <img src={InputError} alt="" />
                            ) : (
                              <img src={InputChecked} alt="" />
                            )
                          }
                        />
                      </Col>
                      <Col xs={24} md={18} lg={16}>
                        <Input
                          type="checkbox"
                          label={TRANSLATION.TELETRANSMISSION}
                          checked={
                            !get(
                              values,
                              "spouse.identity.fields.teletransmission",
                              false,
                            )
                          }
                          {...bindInputProps({
                            name: "spouse.identity.fields.teletransmission",
                            values,
                            ...formProps,
                          })}
                          onChange={({ target: { checked } }) => {
                            setFieldValue(
                              "spouse.identity.fields.teletransmission",
                              !checked,
                            );
                          }}
                          disabled={locked}
                        />
                      </Col>
                    </Row>
                  </div>
                )}

                {get(values, "children", []).map((_, i) => {
                  const currentChild = get(values, `children.${i}`, {});
                  const previousChild = get(values, `children.${i - 1}`, {});

                  const isFirst = i === 0;
                  const spouseReady =
                    !get(values, "spouse") ||
                    (get(values, "spouse.identity.user.firstname") &&
                      get(values, "spouse.identity.user.lastname"));

                  const canShow =
                    (isFirst && spouseReady) ||
                    (!isFirst &&
                      get(previousChild, "identity.user.firstname") &&
                      get(previousChild, "identity.user.lastname"));

                  if (!canShow) return null;

                  return (
                    <div
                      key={`child_${i}`}
                      className="lmfRelated__persons--children"
                    >
                      <span className="lmfRelated__persons--children_title">
                        {TRANSLATION.CHILDREN[`COUNT_${i + 1}`]}
                      </span>
                      <Row gutter={[16, 16]}>
                        <Col xs={24}>
                          <Radio
                            type="simple"
                            disabled={false}
                            values={[
                              {
                                value: GENDER.MALE,
                                label: TRANSLATION.GENDER.OPTIONS.MR,
                              },
                              {
                                value: GENDER.FEMALE,
                                label: TRANSLATION.GENDER.OPTIONS.MRS,
                              },
                            ]}
                            {...bindInputProps({
                              name: `children.${i}.identity.user.gender`,
                              values,
                              setFieldValue,
                              ...formProps,
                            })}
                            onChange={(value) =>
                              setFieldValue(
                                `children.${i}.identity.user.gender`,
                                value,
                              )
                            }
                          />
                        </Col>

                        <Col xs={24} md={12}>
                          <Input
                            label={TRANSLATION.LAST_NAME.LABEL}
                            placeholder={TRANSLATION.LAST_NAME.PLACEHOLDER}
                            {...bindWebTextFieldInputProps({
                              name: `children.${i}.identity.user.lastname`,
                              values,
                              setFieldValue,
                              ...formProps,
                            })}
                            className={classNames({
                              valid: get(
                                values,
                                `children.${i}.identity.user.lastname`,
                              ),
                            })}
                            suffix={
                              get(
                                formProps.errors,
                                `children.${i}.identity.user.lastname`,
                              ) ? (
                                <img src={InputError} alt="" />
                              ) : (
                                <img src={InputChecked} alt="" />
                              )
                            }
                            disabled={locked}
                          />
                        </Col>

                        <Col xs={24} md={12}>
                          <Input
                            label={TRANSLATION.FIRST_NAME.LABEL}
                            placeholder={TRANSLATION.FIRST_NAME.PLACEHOLDER}
                            {...bindWebTextFieldInputProps({
                              name: `children.${i}.identity.user.firstname`,
                              values,
                              setFieldValue,
                              ...formProps,
                            })}
                            className={classNames({
                              valid: get(
                                values,
                                `children.${i}.identity.user.firstname`,
                              ),
                            })}
                            suffix={
                              get(
                                formProps.errors,
                                `children.${i}.identity.user.firstname`,
                              ) ? (
                                <img src={InputError} alt="" />
                              ) : (
                                <img src={InputChecked} alt="" />
                              )
                            }
                            disabled={locked}
                          />
                        </Col>
                        <Col xs={24} sm={24} md={24} lg={24}>
                          <Select
                            label={TRANSLATION.BIRTH_COUNTRY.LABEL}
                            placeholder={TRANSLATION.BIRTH_COUNTRY.PLACEHOLDER}
                            options={COUNTRIES.map((name) => ({
                              label: name,
                              value: name,
                            }))}
                            showSearch
                            allowClear
                            defaultValue={get(
                              initialValues,
                              `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                            )}
                            {...bindInputProps({
                              name: `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                              values,
                              ...formProps,
                            })}
                            className={classNames({
                              valid: get(
                                values,
                                `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                              ),
                            })}
                            onChange={(value) => {
                              if (value !== "France")
                                setFieldValue(
                                  `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                                  "Autre/Etranger",
                                );
                              setFieldValue(
                                `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                                value,
                              );
                            }}
                            suffix={
                              get(
                                formProps.errors,
                                `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                              ) ? (
                                <img src={InputError} alt="" />
                              ) : (
                                <img src={InputChecked} alt="" />
                              )
                            }
                          />
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                          <Input
                            label={TRANSLATION.BIRTH_CITY.LABEL}
                            placeholder={TRANSLATION.BIRTH_CITY.PLACEHOLDER}
                            {...bindWebTextFieldInputProps({
                              name: `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                              values,
                              setFieldValue,
                              ...formProps,
                            })}
                            className={classNames({
                              valid: get(
                                values,
                                `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                              ),
                            })}
                            suffix={
                              get(
                                formProps.errors,
                                `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                              ) ? (
                                <img src={InputError} alt="" />
                              ) : (
                                <img src={InputChecked} alt="" />
                              )
                            }
                          />
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                          <Select
                            label={TRANSLATION.BIRTH_DEPARTMENT.LABEL}
                            placeholder={
                              TRANSLATION.BIRTH_DEPARTMENT.PLACEHOLDER
                            }
                            showSearch
                            allowClear
                            options={FRANCE_DEPARTMENTS.map(
                              ({ code, name }) => ({
                                label: `${code && `(${code})`} ${name}`,
                                value: name,
                              }),
                            )}
                            defaultValue={get(
                              initialValues,
                              `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                            )}
                            {...bindInputProps({
                              name: `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                              values,
                              ...formProps,
                            })}
                            onChange={(value) => {
                              setFieldValue(
                                `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                                value,
                              );
                            }}
                            className={classNames({
                              valid: get(
                                values,
                                `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                              ),
                            })}
                            suffix={
                              get(
                                formProps.errors,
                                `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                              ) ? (
                                <img src={InputError} alt="" />
                              ) : (
                                <img src={InputChecked} alt="" />
                              )
                            }
                          />
                        </Col>
                        <Col xs={24} sm={24} md={24} lg={24}>
                          <Input
                            label={TRANSLATION.SOCIAL_SECURITY_NUMBER.LABEL}
                            placeholder={
                              TRANSLATION.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                            }
                            trim={true}
                            defaultValue={get(
                              initialValues,
                              `children.${i}.identity.socialSecurityNumber`,
                            )}
                            {...bindInputProps({
                              name: `children.${i}.identity.socialSecurityNumber`,
                              values,
                              ...formProps,
                            })}
                            className={classNames({
                              valid: get(
                                values,
                                `children.${i}.identity.socialSecurityNumber`,
                              ),
                            })}
                            suffix={
                              get(
                                formProps.errors,
                                `children.${i}.identity.socialSecurityNumber`,
                              ) ? (
                                <img src={InputError} alt="" />
                              ) : (
                                <img src={InputChecked} alt="" />
                              )
                            }
                          />
                        </Col>
                        <Col xs={24} md={18} lg={16}>
                          <Input
                            type="checkbox"
                            label={TRANSLATION.TELETRANSMISSION}
                            checked={
                              !get(
                                values,
                                `children.${i}.identity.fields.teletransmission`,
                                false,
                              )
                            }
                            {...bindInputProps({
                              name: `children.${i}.identity.fields.teletransmission`,
                              values,
                              ...formProps,
                            })}
                            onChange={({ target: { checked } }) => {
                              setFieldValue(
                                `children.${i}.identity.fields.teletransmission`,
                                !checked,
                              );
                            }}
                            disabled={locked}
                          />
                        </Col>
                      </Row>
                    </div>
                  );
                })}
                {/* {get(values, "children", []).map((_, i) => (
          <div key={`child_${i}`} className="lmfRelated__persons--children">
            <span className="lmfRelated__persons--children_title">
              {TRANSLATION.CHILDREN[`COUNT_${i + 1}`]}
            </span>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <Radio
                  type="simple"
                  disabled={false}
                  values={[
                    {
                      value: GENDER.MALE,
                      label: TRANSLATION.GENDER.OPTIONS.MR,
                    },
                    {
                      value: GENDER.FEMALE,
                      label: TRANSLATION.GENDER.OPTIONS.MRS,
                    },
                  ]}
                  {...bindInputProps({
                    name: `children.${i}.identity.user.gender`,
                    values,
                    setFieldValue,
                    ...formProps,
                  })}
                  onChange={(value) => {
                    setFieldValue(`children.${i}.identity.user.gender`, value);
                  }}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Input
                  label={TRANSLATION.LAST_NAME.LABEL}
                  placeholder={TRANSLATION.LAST_NAME.PLACEHOLDER}
                  {...bindInputProps({
                    name: `children.${i}.identity.user.lastname`,
                    values,
                    ...formProps,
                  })}
                  className={classNames({
                    valid: get(values, "children.${i}.identity.user.lastname"),
                  })}
                  suffix={
                    get(
                      formProps.errors,
                      `children.${i}.identity.user.lastname`
                    ) ? (
                      <img src={InputError} alt="" />
                    ) : (
                      <img src={InputChecked} alt="" />
                    )
                  }
                  disabled={locked}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Input
                  label={TRANSLATION.FIRST_NAME.LABEL}
                  placeholder={TRANSLATION.FIRST_NAME.PLACEHOLDER}
                  {...bindInputProps({
                    name: `children.${i}.identity.user.firstname`,
                    values,
                    ...formProps,
                  })}
                  className={classNames({
                    valid: get(values, "children.${i}.identity.user.firstname"),
                  })}
                  suffix={
                    get(
                      formProps.errors,
                      `children.${i}.identity.user.firstname`
                    ) ? (
                      <img src={InputError} alt="" />
                    ) : (
                      <img src={InputChecked} alt="" />
                    )
                  }
                  disabled={locked}
                />
              </Col>
            </Row>
          </div>
        ))} */}
              </div>
            </Col>
          </Row>
        </div>

        <ActionButtons
          // disabled={!isEmpty(formProps.errors)}
          disabled={!isValid}
          onNext={handleSubmit}
          buttonText={TRANSLATION.NEXT}
          {...(isMobile && { onBack })}
          className={classNames({
            "w-100": isMobile,
          })}
        />
      </Layout>
    </>
  );
};

export default View;
